<template>
  <div class="receive-go-component">
    <template v-if="hasUnlockedRegions">
      <p class="text-muted mb-4">
        {{ $t("hosted_accounts.receive_new_gos_description") }}
      </p>

      <div class="country-selector-step mb-4">
        <label class="form-label fw-semibold mb-2">
          <FontAwesomeIcon icon="fas fa-globe" class="me-2 text-secondary" />
          {{ $t("hosted_accounts.select_country") }}
        </label>
        <div class="country-selector-row">
          <HostedAccountSelectorComponent
            v-model:selectedCountry="selectedCountry"
            :company="props.company"
            @accounts-loaded="handleAccountsLoaded"
          />
        </div>
      </div>

      <template v-if="selectedCountry">
        <hr class="mb-4" />
        <HostedAccountReceiveComponent
          :company="props.company"
          :selectedCountry="selectedCountry"
          :registryAccounts="registryAccounts"
        />
      </template>

      <div
        v-else-if="accountsLoaded && !hasAvailableHostedAccounts"
        class="empty-state text-center py-5"
      >
        <FontAwesomeIcon
          icon="fas fa-inbox"
          class="mb-3 text-muted"
          size="2x"
        />
        <p class="text-muted mb-0">
          {{ $t("hosted_accounts.no_regions_unlocked") }}
        </p>
      </div>

      <div v-else class="empty-state text-center py-5">
        <FontAwesomeIcon
          icon="fas fa-hand-pointer"
          class="mb-3 text-muted"
          size="2x"
        />
        <p class="text-muted mb-0">
          {{ $t("hosted_accounts.select_country_first") }}
        </p>
      </div>
    </template>

    <div v-else class="empty-state text-center py-5">
      <FontAwesomeIcon icon="fas fa-inbox" class="mb-3 text-muted" size="2x" />
      <p class="text-muted mb-0">
        {{ $t("hosted_accounts.no_regions_unlocked") }}
      </p>
    </div>

    <div
      class="mt-4 d-flex align-items-center gap-3 p-3 border rounded-2 bg-light"
    >
      <div class="flex-grow-1">
        <p class="fw-semibold mb-0">
          {{
            hasUnlockedRegions
              ? $t("hosted_accounts.contact_us_title")
              : $t("hosted_accounts.contact_us_title_no_accounts")
          }}
        </p>
        <p class="text-muted small mb-0">
          {{
            hasUnlockedRegions
              ? $t("hosted_accounts.contact_us_description")
              : $t("hosted_accounts.contact_us_description_no_accounts")
          }}
        </p>
      </div>
      <button
        type="button"
        class="btn btn-outline-primary flex-shrink-0"
        @click="openHelpCrunch"
      >
        {{ $t("hosted_accounts.contact_us_button") }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { SolderaRegistryAccount } from "@/dashboard/inventory_management/company_orders/types/virtualAccountTypes";
import { useHelpCrunch } from "@/composables/useHelpCrunch";
import type { Company } from "@/types";
import { computed, ref } from "vue";
import HostedAccountReceiveComponent from "./HostedAccountReceiveComponent.vue";
import HostedAccountSelectorComponent from "./HostedAccountSelectorComponent.vue";

const props = defineProps<{
  company: Company;
}>();

const { openHelpCrunch } = useHelpCrunch();

const selectedCountry = ref<string>("");
const registryAccounts = ref<SolderaRegistryAccount[]>([]);
const accountsLoaded = ref(false);

const hasUnlockedRegions = computed(
  () => (props.company.unlocked_hosted_account_regions?.length ?? 0) > 0
);

const hasAvailableHostedAccounts = computed(() => {
  const unlocked = props.company.unlocked_hosted_account_regions ?? [];
  const registryCodes = new Set(
    registryAccounts.value.map((acc) => acc.country_code)
  );
  return unlocked.some((code) => registryCodes.has(code));
});

const handleAccountsLoaded = (accounts: SolderaRegistryAccount[]) => {
  registryAccounts.value = accounts;
  accountsLoaded.value = true;
};
</script>

<style scoped lang="scss">
.receive-go-component {
  .empty-state {
    opacity: 0.8;
  }

  .country-selector-row {
    max-width: 400px;
  }
}
</style>
